import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { Database } from '@hive/shared';
import { supabase } from '@/lib/supabase';

type TableName = keyof Database['public']['Tables'];

/**
 * Invalidates `queryKey` whenever a row in `table` changes, via Supabase Realtime.
 * Changes made while the connection was down aren't replayed, so when it comes
 * back (subscribed again) the query is refreshed to catch up.
 */
export function useRealtimeInvalidation(table: TableName, queryKey: QueryKey): void {
  const queryClient = useQueryClient();
  const keyString = JSON.stringify(queryKey);

  useEffect(() => {
    const key = JSON.parse(keyString) as QueryKey;
    const client = supabase();
    let subscribedBefore = false;
    const channel = client
      .channel(`db:${table}:${keyString}`)
      .on('postgres_changes', { event: '*', schema: 'public', table }, () => {
        void queryClient.invalidateQueries({ queryKey: key });
      })
      .subscribe((status) => {
        if (status !== 'SUBSCRIBED') return;
        if (subscribedBefore) void queryClient.invalidateQueries({ queryKey: key });
        subscribedBefore = true;
      });
    return () => {
      void client.removeChannel(channel);
    };
  }, [table, keyString, queryClient]);
}
